"use client"

import { Sparkles, Heart, PlusCircle, Shield, Bone, PawPrint, Camera, Home, Menu } from "lucide-react"
import { Button } from "@/components/ui/button"
import { motion } from "framer-motion"
import { useAnalysis } from '../../AnalysisContext'
import { useNavigate, useLocation } from "react-router-dom"
import { useState, useEffect } from "react"
import { cn } from "@/lib/utils"

export function MobileNav() {
    const { analysisData, breedGuide, clearContext } = useAnalysis()
    const navigate = useNavigate()
    const location = useLocation()
    const [isScrolled, setIsScrolled] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)

    // Track scroll position for UI effects
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20)
        }
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    // Function to open the sidebar
    const openSidebar = () => {
        // Find and click the sidebar trigger button
        const sidebarTrigger = document.querySelector('.sidebar-trigger')
        if (sidebarTrigger) {
            sidebarTrigger.click()
        }
    }

    // Handle new analysis button click
    const handleNewAnalysis = () => {
        if (location.pathname === "/dog-analyzer") {
        
            window.location.href = "/dog-analyzer";
            return;
        }

        if (analysisData) {
            // If there's existing analysis data, open the modal
            const modalTrigger = document.querySelector('.new-analysis-modal-trigger')
            if (modalTrigger) {
                modalTrigger.click()
            } else {
                // Fallback to direct navigation if modal trigger not found
                clearContext();
                navigate("/dog-analyzer")
            }
        } else {
            // If no existing analysis, go directly to analyzer
            clearContext();
            navigate("/dog-analyzer")
        }
    }

    // Handle navigation to a specific route
    const handleNavigation = (path) => {
        console.log(`Navigating to: ${path}`)
        navigate(path)
    }

    if (!analysisData) {
        return (
            <>
                {/* Enhanced Floating Action Button with modern styling */}
                <motion.div
                    initial={{ scale: 0, y: 20 }}
                    animate={{ scale: 1, y: 0 }}
                    transition={{
                        type: "spring",
                        stiffness: 260,
                        damping: 20
                    }}
                    className="fixed bottom-6 right-0 left-0 z-40 flex flex-col items-center justify-center gap-2 md:hidden"
                >
                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-sm font-medium text-amber-800/90 text-center px-4 py-1 bg-white/80 backdrop-blur-sm rounded-full shadow-sm"
                    >
                        Analizar nueva imagen
                    </motion.p>
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="relative"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full blur-xl opacity-30 animate-pulse"></div>
                        <Button
                            onClick={handleNewAnalysis}
                            className="relative h-14 w-14 rounded-full bg-gradient-to-br from-amber-500 via-amber-500 to-amber-600 hover:from-amber-400 hover:via-amber-500 hover:to-amber-600 shadow-lg hover:shadow-amber-500/25 transition-all duration-300 border border-amber-400/20"
                        >
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.4),transparent_50%)] rounded-full"></div>
                            <Camera className="h-6 w-6 text-white drop-shadow-sm" />
                        </Button>
                    </motion.div>
                </motion.div>
            </>
        )
    }

    // Determine active tab
    const getActiveStatus = (path) => {
        // Handle root path as overview
        if (path === "/overview" && (location.pathname === "/" || location.pathname === "/overview")) {
            return true
        }
        return location.pathname === path
    }

    return (
        <>
            {/* Floating Breed Button with enhanced styling */}
            {breedGuide && location.pathname !== "/characteristics" && (
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    whileTap={{ scale: 0.9 }}
                    className="fixed left-5 bottom-20 z-40 md:hidden"
                >
                    <Button
                        variant="ghost"
                        onClick={() => handleNavigation("/characteristics")}
                        className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 hover:from-emerald-200 hover:to-emerald-300 backdrop-blur-sm shadow-md hover:shadow-lg transition-all duration-200"
                    >
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.2),transparent_70%)] rounded-full"></div>
                        <PawPrint className="h-5 w-5 text-emerald-600" />
                    </Button>
                </motion.div>
            )}

            {/* Main Navigation Bar - Enhanced with modern styling */}
            <motion.div
                initial={{ y: 100 }}
                animate={{ y: 0 }}
                className="fixed bottom-0 left-0 right-0 z-40 md:hidden"
            >
                {/* Blurred background with subtle gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/95 to-white/90 backdrop-blur-md border-t border-amber-100/30 shadow-[0_-1px_10px_rgba(0,0,0,0.03)]"></div>
                
                <nav className="relative flex items-center justify-between px-2 py-3">
                    {/* General Button */}
                    <Button 
                        variant="ghost"
                        className="flex-1 flex flex-col items-center gap-1 rounded-xl px-3 py-1.5 min-h-0 h-auto z-50
                            transition-all duration-200 cursor-pointer bg-transparent hover:bg-transparent"
                        onClick={() => handleNavigation("/overview")}
                    >
                        <div className={cn(
                            "relative transition-all duration-200",
                            getActiveStatus("/overview") ? "scale-110" : "scale-100"
                        )}>
                            <Sparkles className={cn(
                                "h-5 w-5 transition-colors",
                                getActiveStatus("/overview") ? "text-amber-600" : "text-amber-500/80"
                            )} />
                            {getActiveStatus("/overview") && (
                                <motion.div 
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute -top-1 -right-1 h-2 w-2 bg-amber-400 rounded-full"
                                />
                            )}
                        </div>
                        <span className={cn(
                            "text-xs font-medium transition-all duration-200",
                            getActiveStatus("/overview") ? "text-amber-900" : "text-amber-700/70"
                        )}>
                            General
                        </span>
                    </Button>
                    
                    {/* Salud Button */}
                    <Button 
                        variant="ghost"
                        className="flex-1 flex flex-col items-center gap-1 rounded-xl px-3 py-1.5 min-h-0 h-auto z-50
                            transition-all duration-200 cursor-pointer bg-transparent hover:bg-transparent"
                        onClick={() => handleNavigation("/health")}
                    >
                        <div className={cn(
                            "relative transition-all duration-200",
                            getActiveStatus("/health") ? "scale-110" : "scale-100"
                        )}>
                            <Heart className={cn(
                                "h-5 w-5 transition-colors",
                                getActiveStatus("/health") ? "text-amber-600" : "text-amber-500/80"
                            )} />
                            {getActiveStatus("/health") && (
                                <motion.div 
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute -top-1 -right-1 h-2 w-2 bg-amber-400 rounded-full"
                                />
                            )}
                        </div>
                        <span className={cn(
                            "text-xs font-medium transition-all duration-200",
                            getActiveStatus("/health") ? "text-amber-900" : "text-amber-700/70"
                        )}>
                            Salud
                        </span>
                    </Button>
                    
                    {/* New Analysis Button - Center FAB */}
                    <div className="-mt-6 flex-1 flex justify-center">
                        <Button
                            onClick={handleNewAnalysis}
                            className="flex flex-col items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg hover:shadow-xl transition-all duration-300 z-40"
                        >
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.2),transparent_70%)] rounded-full"></div>
                            <Camera className="h-6 w-6 text-white" />
                        </Button>
                    </div>
                    
                    {/* Cuidados Button */}
                    <Button 
                        variant="ghost"
                        className="flex-1 flex flex-col items-center gap-1 rounded-xl px-3 py-1.5 min-h-0 h-auto z-50
                            transition-all duration-200 cursor-pointer bg-transparent hover:bg-transparent"
                        onClick={() => handleNavigation("/care")}
                    >
                        <div className={cn(
                            "relative transition-all duration-200",
                            getActiveStatus("/care") ? "scale-110" : "scale-100"
                        )}>
                            <Shield className={cn(
                                "h-5 w-5 transition-colors",
                                getActiveStatus("/care") ? "text-amber-600" : "text-amber-500/80"
                            )} />
                            {getActiveStatus("/care") && (
                                <motion.div 
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute -top-1 -right-1 h-2 w-2 bg-amber-400 rounded-full"
                                />
                            )}
                        </div>
                        <span className={cn(
                            "text-xs font-medium transition-all duration-200",
                            getActiveStatus("/care") ? "text-amber-900" : "text-amber-700/70"
                        )}>
                            Cuidados
                        </span>
                    </Button>
                    
                    {/* Nutrición Button */}
                    <Button 
                        variant="ghost"
                        className="flex-1 flex flex-col items-center gap-1 rounded-xl px-3 py-1.5 min-h-0 h-auto z-50
                            transition-all duration-200 cursor-pointer bg-transparent hover:bg-transparent"
                        onClick={() => handleNavigation("/nutrition")}
                    >
                        <div className={cn(
                            "relative transition-all duration-200",
                            getActiveStatus("/nutrition") ? "scale-110" : "scale-100"
                        )}>
                            <Bone className={cn(
                                "h-5 w-5 transition-colors",
                                getActiveStatus("/nutrition") ? "text-amber-600" : "text-amber-500/80"
                            )} />
                            {getActiveStatus("/nutrition") && (
                                <motion.div 
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute -top-1 -right-1 h-2 w-2 bg-amber-400 rounded-full"
                                />
                            )}
                        </div>
                        <span className={cn(
                            "text-xs font-medium transition-all duration-200",
                            getActiveStatus("/nutrition") ? "text-amber-900" : "text-amber-700/70"
                        )}>
                            Nutrición
                        </span>
                    </Button>
                </nav>
            </motion.div>
        </>
    )
}